import { useState } from "react";
import { Check, X } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal, Kicker, SectionTitle } from "./Reveal";
import { Slider } from "../ui/slider";

const IMGS = ["/img/step1.jpg", "/img/step2.jpg", "/img/step3.jpg"];

export const HowTo = () => {
  const { t } = useLang();
  const [ml, setMl] = useState([750]);
  const drops = Math.min(20, Math.round((ml[0] / 1000) * 20 * 1.33));
  return (
    <section id="use" className="bg-[#FBF7EF] py-24 md:py-32" data-testid="howto-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="mb-14 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-3"><Kicker>{t.use.kicker}</Kicker></div>
          <SectionTitle className="lg:col-span-9">{t.use.title}</SectionTitle>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {t.use.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1} className={i === 1 ? "md:mt-16" : ""}>
              <div className="group" data-testid={`howto-step-${i + 1}`}>
                <div className="relative aspect-[4/5] overflow-hidden rounded-3xl">
                  <img src={IMGS[i]} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-5 top-4 font-display text-7xl text-[#FBF7EF] drop-shadow-lg">0{i + 1}</span>
                </div>
                <p className="mt-5 text-[#073B4C]">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 grid items-center gap-8 rounded-3xl bg-[#0A7189] p-8 text-[#FBF7EF] md:grid-cols-2 md:p-10">
          <div>
            <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-[#7ED9D2]">{t.use.calcTitle}</p>
            <p className="mt-4 text-sm text-[#FBF7EF]/80">{t.use.calcLabel}: <span className="font-mono-x text-[#FBF7EF]" data-testid="calc-ml">{ml[0]} ml</span></p>
            <Slider value={ml} onValueChange={setMl} min={250} max={1000} step={50} className="mt-5" data-testid="calc-slider" />
          </div>
          <div className="md:text-right">
            <p className="font-display text-8xl leading-none text-[#7ED9D2]" data-testid="calc-drops">{drops}</p>
            <p className="text-sm text-[#FBF7EF]/80">{t.use.calcResult}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Compare = () => {
  const { t } = useLang();
  return (
    <section className="border-t border-[#073B4C]/10 bg-[#E4F2EF] py-24 md:py-32" data-testid="compare-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="mb-12 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-3"><Kicker>{t.compare.kicker}</Kicker></div>
          <SectionTitle className="lg:col-span-9">{t.compare.title}</SectionTitle>
        </Reveal>
        <div className="hidden grid-cols-12 gap-4 border-b border-[#073B4C]/20 pb-4 font-mono-x text-xs uppercase tracking-[0.18em] md:grid">
          <span className="col-span-3 text-[#3F5F68]">&nbsp;</span>
          <span className="col-span-5 text-[#0A7189]">{t.compare.us}</span>
          <span className="col-span-4 text-[#3F5F68]">{t.compare.them}</span>
        </div>
        {t.compare.rows.map((r, i) => (
          <Reveal key={r.k} delay={i * 0.06}>
            <div className="grid gap-3 border-b border-[#073B4C]/15 py-6 md:grid-cols-12 md:gap-4" data-testid={`compare-row-${i}`}>
              <p className="font-display text-2xl md:col-span-3">{r.k}</p>
              <p className="flex gap-3 md:col-span-5"><Check className="mt-0.5 h-5 w-5 shrink-0 text-[#0A7189]" /><span><span className="block font-mono-x text-[11px] uppercase tracking-wider text-[#0A7189] md:hidden">{t.compare.us}</span>{r.a}</span></p>
              <p className="flex gap-3 text-[#3F5F68] md:col-span-4"><X className="mt-0.5 h-5 w-5 shrink-0 text-[#C2502F]" /><span><span className="block font-mono-x text-[11px] uppercase tracking-wider md:hidden">{t.compare.them}</span>{r.b}</span></p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
